/* ── useKeyboardShortcuts ─────────────────────────────────────
   Global keyboard shortcuts for the Standards Compliance Hub.

   Shortcuts:
     Cmd/Ctrl+K       → focus the query input / open command palette
     Cmd/Ctrl+Enter   → send query
     Up arrow         → fill with last query (when input empty)
     Escape           → close any open panel
     Cmd/Ctrl+N       → new chat
     Cmd/Ctrl+/       → show keyboard shortcuts panel
     Cmd/Ctrl+B       → open Research sidebar (Bookmarks tab)
     Cmd/Ctrl+H       → open Research sidebar (Highlights tab)
     Cmd/Ctrl+,       → open settings
     Cmd/Ctrl+E       → open Edition Compare
   ────────────────────────────────────────────────────────────── */

import { useEffect, useCallback } from 'react';

interface ShortcutActions {
  /** Focus the query textarea (or open command palette if empty) */
  focusInput: () => void;
  /** Send the current query (if any) */
  sendQuery: () => void;
  /** Fill the input with the last user query */
  fillLastQuery: () => void;
  /** Close any open panel (sidebar, corpus browser, settings) */
  closePanel: () => void;
  /** Start a new chat */
  newChat: () => void;
  /** Returns true if the query input is focused and empty */
  isInputFocusedAndEmpty: () => boolean;
  /** Toggle keyboard shortcuts panel */
  toggleShortcuts?: () => void;
  /** Open Research sidebar on Bookmarks tab */
  openResearchBookmarks?: () => void;
  /** Open Research sidebar on Highlights tab */
  openResearchHighlights?: () => void;
  /** Toggle settings panel */
  toggleSettings?: () => void;
  /** Open Edition Compare */
  openCompare?: () => void;
}

export default function useKeyboardShortcuts(actions: ShortcutActions): void {
  const handler = useCallback(
    (e: KeyboardEvent) => {
      const isMod = e.metaKey || e.ctrlKey;
      const target = e.target as HTMLElement;
      const isInInput =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable;

      // Cmd/Ctrl+/ → toggle keyboard shortcuts panel
      if (isMod && e.key === '/') {
        e.preventDefault();
        actions.toggleShortcuts?.();
        return;
      }

      // Cmd/Ctrl+K → focus query input
      if (isMod && e.key === 'k') {
        e.preventDefault();
        actions.focusInput();
        return;
      }

      // Cmd/Ctrl+Enter → send query
      if (isMod && e.key === 'Enter') {
        e.preventDefault();
        actions.sendQuery();
        return;
      }

      // Cmd/Ctrl+N → new chat
      if (isMod && e.key === 'n') {
        // Only intercept if not in a native context menu situation
        e.preventDefault();
        actions.newChat();
        return;
      }

      // Cmd/Ctrl+B → open Research sidebar (Bookmarks tab)
      if (isMod && e.key === 'b') {
        e.preventDefault();
        actions.openResearchBookmarks?.();
        return;
      }

      // Cmd/Ctrl+H → open Research sidebar (Highlights tab)
      if (isMod && e.key === 'h') {
        e.preventDefault();
        actions.openResearchHighlights?.();
        return;
      }

      // Cmd/Ctrl+, → open settings
      if (isMod && e.key === ',') {
        e.preventDefault();
        actions.toggleSettings?.();
        return;
      }

      // Cmd/Ctrl+E → open Edition Compare
      if (isMod && e.key === 'e') {
        e.preventDefault();
        actions.openCompare?.();
        return;
      }

      // Escape → close any open panel
      if (e.key === 'Escape') {
        actions.closePanel();
        return;
      }

      // Up arrow in empty input → fill with last query
      if (e.key === 'ArrowUp' && isInInput && actions.isInputFocusedAndEmpty()) {
        e.preventDefault();
        actions.fillLastQuery();
        return;
      }
    },
    [actions],
  );

  useEffect(() => {
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [handler]);
}
